import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/shared/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  FileBarChart,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Eye,
  Copy,
  Trash2,
  Star,
  StarOff,
  Clock,
  Calendar,
  Settings,
  ChevronDown,
  FileText,
  Download,
  Mail,
  Play,
  Archive,
  Filter,
  LayoutList,
  ShoppingCart,
  Package,
  Warehouse,
  RotateCcw,
  Box,
  Truck,
  CreditCard,
  Wallet,
  Landmark,
  ArrowLeftRight,
  Receipt,
  Users,
  Car,
  Route,
  Share2,
  Store
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const typeIcons = {
  'shopping-cart': ShoppingCart,
  'package-plus': Package,
  'warehouse': Warehouse,
  'rotate-ccw': RotateCcw,
  'box': Box,
  'truck': Truck,
  'credit-card': CreditCard,
  'wallet': Wallet,
  'landmark': Landmark,
  'arrow-left-right': ArrowLeftRight,
  'file-text': FileText,
  'file-check': FileText,
  'receipt': Receipt,
  'users': Users,
  'package': Package,
  'car': Car,
  'user': Users,
  'route': Route,
  'share-2': Share2,
  'store': Store
};

export const ReportBuilder = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [activeTab, setActiveTab] = useState('reports');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, typesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/report-builder/reports`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/report-builder/types`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/report-builder/stats`, { headers: getAuthHeaders() })
      ]);

      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setReports(data.reports || []);
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setReportTypes(data.report_types || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Tem certeza que deseja excluir este relatório?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/report-builder/reports/${reportId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Relatório excluído');
        fetchData();
      }
    } catch (err) {
      toast.error('Erro ao excluir relatório');
    }
  };

  const handleDuplicate = async (reportId) => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/reports/${reportId}/duplicate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Relatório duplicado');
        fetchData();
      }
    } catch (err) {
      toast.error('Erro ao duplicar relatório');
    }
  };

  const handleToggleFavorite = async (reportId, currentFavorite) => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/reports/${reportId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_favorite: !currentFavorite })
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      toast.error('Erro ao atualizar favorito');
    }
  };

  const handleExecute = async (reportId) => {
    navigate(`/reports/preview/${reportId}`);
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || report.type_slug === selectedType;
    return matchesSearch && matchesType;
  });

  const getTypeIcon = (iconName) => {
    const Icon = typeIcons[iconName] || FileBarChart;
    return Icon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-builder">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
              <FileBarChart className="h-7 w-7 text-white" />
            </div>
            Gerador de Relatórios
          </h1>
          <p className="text-muted-foreground mt-1">
            Crie, gerencie e agende relatórios personalizados
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Settings className="h-4 w-4" />
                Gerenciar
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/reports/types')}>
                <LayoutList className="h-4 w-4 mr-2" />
                Gerenciar Tipos
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/reports/permissions')}>
                <Users className="h-4 w-4 mr-2" />
                Permissões
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/reports/schedules')}>
                <Clock className="h-4 w-4 mr-2" />
                Agendamentos
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-2 bg-gradient-to-r from-blue-500 to-indigo-600">
                <Plus className="h-4 w-4" />
                Criar Novo
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {reportTypes.slice(0, 10).map((type) => {
                const Icon = getTypeIcon(type.icon);
                return (
                  <DropdownMenuItem 
                    key={type.id}
                    onClick={() => navigate(`/reports/new?type=${type.id}`)}
                  >
                    <Icon className="h-4 w-4 mr-2" style={{ color: type.color }} />
                    {type.name}
                  </DropdownMenuItem>
                );
              })}
              {reportTypes.length > 10 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/reports/new')}>
                    Ver todos os tipos...
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Meus Relatórios</p>
                <p className="text-2xl font-bold">{stats?.total_reports || 0}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileBarChart className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tipos Disponíveis</p>
                <p className="text-2xl font-bold">{stats?.report_types || 0}</p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <LayoutList className="h-5 w-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Execuções (Mês)</p>
                <p className="text-2xl font-bold">{stats?.executions_this_month || 0}</p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Play className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Agendamentos Ativos</p>
                <p className="text-2xl font-bold">{stats?.active_schedules || 0}</p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="reports" className="gap-2">
            <FileText className="h-4 w-4" /> Meus Relatórios
          </TabsTrigger>
          <TabsTrigger value="favorites" className="gap-2">
            <Star className="h-4 w-4" /> Favoritos
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="gap-2">
            <Calendar className="h-4 w-4" /> Agendados
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar relatórios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Filter className="h-4 w-4" />
                  {selectedType === 'all' ? 'Todos os Tipos' : reportTypes.find(t => t.slug === selectedType)?.name}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto">
                <DropdownMenuItem onClick={() => setSelectedType('all')}>
                  Todos os Tipos
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {reportTypes.map((type) => {
                  const Icon = getTypeIcon(type.icon);
                  return (
                    <DropdownMenuItem 
                      key={type.id}
                      onClick={() => setSelectedType(type.slug)}
                    >
                      <Icon className="h-4 w-4 mr-2" style={{ color: type.color }} />
                      {type.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Reports Table */}
          {filteredReports.length === 0 ? (
            <Card className="card-elevated border-dashed">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                  <FileBarChart className="h-10 w-10 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Nenhum Relatório</h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Crie seu primeiro relatório personalizado para começar.
                </p>
                <Button 
                  className="gap-2 bg-gradient-to-r from-blue-500 to-indigo-600"
                  onClick={() => navigate('/reports/new')}
                >
                  <Plus className="h-4 w-4" />
                  Criar Relatório
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-elevated">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Nome</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Tipo</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Última Execução</th>
                        <th className="text-center py-3 px-4 font-medium text-muted-foreground">Execuções</th>
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReports.map((report) => {
                        const Icon = getTypeIcon(report.type_icon);
                        return (
                          <tr key={report.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => handleToggleFavorite(report.id, report.is_favorite)}
                                  className="text-muted-foreground hover:text-amber-500 transition-colors"
                                >
                                  {report.is_favorite ? (
                                    <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                                  ) : (
                                    <StarOff className="h-4 w-4" />
                                  )}
                                </button>
                                <div>
                                  <p className="font-medium">{report.name}</p>
                                  {report.description && (
                                    <p className="text-xs text-muted-foreground line-clamp-1">{report.description}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge 
                                variant="outline" 
                                className="gap-1"
                                style={{ borderColor: report.type_color, color: report.type_color }}
                              >
                                <Icon className="h-3 w-3" />
                                {report.type_name}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {report.last_run_at 
                                ? new Date(report.last_run_at).toLocaleString('pt-BR')
                                : 'Nunca executado'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant="secondary">{report.run_count || 0}</Badge>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={() => handleExecute(report.id)}
                                  title="Executar"
                                >
                                  <Play className="h-4 w-4 text-emerald-600" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={() => navigate(`/reports/edit/${report.id}`)}
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleExecute(report.id)}>
                                      <Eye className="h-4 w-4 mr-2" />
                                      Visualizar
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate(`/reports/edit/${report.id}`)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Editar
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDuplicate(report.id)}>
                                      <Copy className="h-4 w-4 mr-2" />
                                      Duplicar
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate(`/reports/schedule/${report.id}`)}>
                                      <Calendar className="h-4 w-4 mr-2" />
                                      Agendar
                                    </DropdownMenuItem>
                                    <DropdownMenuItem>
                                      <Mail className="h-4 w-4 mr-2" />
                                      Enviar por Email
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => handleDelete(report.id)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Excluir
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="favorites" className="mt-4">
          <Card className="card-elevated">
            <CardContent className="p-6 text-center text-muted-foreground">
              <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Seus relatórios favoritos aparecerão aqui.</p>
              <p className="text-sm">Clique na estrela ao lado de um relatório para adicioná-lo aos favoritos.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scheduled" className="mt-4">
          <Card className="card-elevated">
            <CardContent className="p-6 text-center text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Relatórios com agendamento ativo aparecerão aqui.</p>
              <p className="text-sm">Agende um relatório para recebê-lo automaticamente por email.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportBuilder;
